import React from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import SectionCard from './SectionCard';

export default function RemoteDesktopSection({ form, setField, provider, fillHeight }) {
    const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
    const orgClusterSettings = useSelector(({ settings }) => settings.settings.orgClusterSettings);

    // Ubuntu 26 cloud defaults (Heidi Desktop on, DCV off) are applied at image-selection
    // time in ImageSelectionSection, where osType is actually defined. That works whether
    // the Remote Desktop card is mounted or not (it only renders in advanced mode), so no
    // osType-watching effect is needed here.

    const hprdEnabled = orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop
        || clusterSettings?.enableHighPerformanceRemoteDesktop;

    const dcvCaption = provider === 'aws'
        ? 'Install DCV remote desktop during deployment (free on AWS EC2)'
        : 'Install DCV remote desktop during deployment (requires license for non-AWS)';

    return (
        <SectionCard icon="desktop_windows" title="Remote Desktop Settings" fillHeight={fillHeight}>
            <Box display="flex" justifyContent="space-between">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.vncTokenAuthentication || false}
                            onChange={(e) => setField('vncTokenAuthentication', e.target.checked)}
                            name="vncTokenAuthentication"
                            color="primary"
                        />
                    }
                    label="Token Authentication"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.vncRemoteClient || false}
                            onChange={(e) => setField('vncRemoteClient', e.target.checked)}
                            name="vncRemoteClient"
                            color="primary"
                        />
                    }
                    label="Remote Client"
                />
            </Box>
            {hprdEnabled && (
                <Box mt={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.dcvEnabled || false}
                                onChange={(e) => setField('dcvEnabled', e.target.checked)}
                                name="dcvEnabled"
                                color="primary"
                            />
                        }
                        label="DCV Remote Desktop"
                    />
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginLeft: 32 }}>
                        {dcvCaption}
                    </Typography>
                </Box>
            )}
            {/* Default Remote Desktop engine. Defaults to "" (Auto). When the
             * user picks Legacy VNC or DCV explicitly, the backend skips its
             * auto-add of Heidi Desktop on Ubuntu 26 - lets users running
             * custom-baked images without Heidi Desktop (E4S, ParaTools,
             * etc.) keep their stack as-is and have the Remote Desktop
             * button respect that choice. */}
            <Box mt={2}>
                <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel id="default-remote-desktop-label">Default Remote Desktop</InputLabel>
                    <Select
                        labelId="default-remote-desktop-label"
                        id="default-remote-desktop"
                        label="Default Remote Desktop"
                        value={form.defaultRemoteProtocol || ''}
                        onChange={(e) => setField('defaultRemoteProtocol', e.target.value)}
                    >
                        <MenuItem value=""><em>Auto (recommended)</em></MenuItem>
                        <MenuItem value="desktop">Heidi Desktop</MenuItem>
                        <MenuItem value="dcv">DCV</MenuItem>
                        <MenuItem value="vnc">Legacy VNC</MenuItem>
                    </Select>
                    <FormHelperText>
                        Auto picks Heidi Desktop on Ubuntu 26 (when installed), DCV/VNC otherwise.
                        Pick Legacy VNC or DCV explicitly to skip Heidi Desktop on a custom-baked image.
                    </FormHelperText>
                </FormControl>
            </Box>
            {/* Heidi Desktop checkbox removed: it's auto-detected from the
             * cluster's OS (Ubuntu 26+) and bake-time component metadata.
             * The Access tab's Remote Desktop dropdown surfaces it
             * automatically when available. */}
        </SectionCard>
    );
}
